import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users, MoreVertical, Key } from 'lucide-react';
import { isAdditionalOfficeId, getOfficeCodeFromVirtualId } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useClientDetail } from './hooks/useClientDetail';
import { ClientProvider } from './ClientContext';
import { MainTab } from './tabs/MainTab';
import { DocsTab } from './tabs/DocsTab';
import { OrdersTab } from './tabs/OrdersTab';
import { InfoTab } from './tabs/InfoTab';
import { MonitoringTab } from './tabs/MonitoringTab';
import { LocationTab } from './tabs/LocationTab';
import { ContactsTab } from './tabs/ContactsTab';
import { ClientTabNav } from './ClientTabNav';
import { getClientTabQuery, getClientTabValue } from './clientTabNavModel';
import { ClientLicensingTab } from '@/features/licensing';
import {
  useActiveVisit,
  useCheckIn,
  useCheckOut,
} from '@/features/visits/hooks/useActiveVisit';
import {
  useMarkVisited,
  useMarkSkipped,
  useUndoCheckIn,
  useSetPriority,
  usePlanToday,
  useCancelVisit,
  useCreateVisit,
} from '@/features/visits/hooks/useWeeklyVisits';
import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientDetailApi } from '@/api/client-detail';
import type { CheckOutPayload } from '@/api/visits';
import { ClientAccessSheet } from '@/features/visits/ClientAccessSheet';
import { VaultSheet } from '@/features/vault/VaultSheet';

interface LocationState {
  clientId?: number;
  officeName?: string;
  officeId?: string;
}

export function ClientPage() {
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accessSheetOpen, setAccessSheetOpen] = useState(false);
  const [vaultSheetOpen, setVaultSheetOpen] = useState(false);

  const activeTab = useMemo(() => {
    return getClientTabValue(searchParams.get('tab'));
  }, [searchParams]);

  const handleTabChange = useCallback((value: string) => {
    setSearchParams(getClientTabQuery(getClientTabValue(value)), { replace: true });
  }, [setSearchParams]);

  // visitTargetId from URL - used for visit operations (may be virtual office ID)
  const visitTargetId = clientIdParam ? parseInt(clientIdParam, 10) : undefined;
  const isVirtualId = visitTargetId ? isAdditionalOfficeId(visitTargetId) : false;

  // From navigation state (available when navigating from visits list)
  const locationState = location.state as LocationState | null;
  const stateClientId = locationState?.clientId;
  const stateOfficeName = locationState?.officeName;
  const stateOfficeId = locationState?.officeId;

  // For virtual IDs without state (direct URL, refresh, bookmark) — resolve via API
  const officeCode = isVirtualId && visitTargetId ? getOfficeCodeFromVirtualId(visitTargetId) : undefined;
  const { data: resolved } = useQuery({
    queryKey: ['resolve-office', officeCode],
    queryFn: () => clientDetailApi.resolveOffice(officeCode!),
    enabled: isVirtualId && !stateClientId && !!officeCode,
    staleTime: Infinity,
  });

  // Derive clientId and officeId from state or resolved data
  const clientId = stateClientId ?? resolved?.clientId ?? (!isVirtualId ? visitTargetId : undefined);
  const officeId = stateOfficeId ?? resolved?.officeId;
  const officeName = stateOfficeName ?? resolved?.officeName;

  // Data fetching (uses real clientId, not virtual)
  const { data: detail, isLoading: detailLoading } = useClientDetail(clientId, officeId);
  const { data: activeVisit } = useActiveVisit();

  // Mutations
  const checkIn = useCheckIn();
  const checkOut = useCheckOut();
  const markVisited = useMarkVisited();
  const markSkipped = useMarkSkipped();
  const undoCheckIn = useUndoCheckIn();
  const setPriority = useSetPriority();
  const planToday = usePlanToday();
  const cancelVisit = useCancelVisit();
  const createVisit = useCreateVisit();

  // Handlers
  const handleCheckIn = useCallback(
    (id: number) => {
      checkIn.mutate({ clientId: id });
    },
    [checkIn]
  );

  const handleCheckOut = useCallback(
    (payload?: CheckOutPayload) => {
      checkOut.mutate(payload, {
        onSuccess: () => {
          navigate('/visits');
        },
      });
    },
    [checkOut, navigate]
  );

  const handleMarkVisited = useCallback(
    (id: number, note?: string) => {
      markVisited.mutate({ clientId: id, note });
    },
    [markVisited]
  );

  const handleMarkSkipped = useCallback(
    (id: number, note?: string) => {
      markSkipped.mutate({ clientId: id, note });
    },
    [markSkipped]
  );

  const handleUndoCheckIn = useCallback(
    (id: number) => {
      undoCheckIn.mutate(id);
    },
    [undoCheckIn]
  );

  const handleSetUrgent = useCallback(
    (id: number) => {
      setPriority.mutate({ visitTargetId: id, priority: 'URGENT' });
    },
    [setPriority]
  );

  const handleRemoveUrgent = useCallback(
    (id: number) => {
      cancelVisit.mutate(id);
    },
    [cancelVisit]
  );

  const handlePlanToday = useCallback(
    (id: number) => {
      planToday.mutate(id);
    },
    [planToday]
  );

  const handleCancelPlanToday = useCallback(
    (id: number) => {
      cancelVisit.mutate(id);
    },
    [cancelVisit]
  );

  const handleScheduleVisit = useCallback(
    (id: number) => {
      createVisit.mutate({ visitTargetId: id });
    },
    [createVisit]
  );

  const isActionLoading =
    checkIn.isPending ||
    checkOut.isPending ||
    markVisited.isPending ||
    markSkipped.isPending ||
    undoCheckIn.isPending ||
    setPriority.isPending ||
    planToday.isPending ||
    cancelVisit.isPending ||
    createVisit.isPending;

  // Need both visitTargetId and clientId to function
  if (!visitTargetId || !clientId) {
    return (
      <div className="flex flex-col min-h-full items-center justify-center">
        <p className="text-muted-foreground">Клиент не найден</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border safe-area-top">
        <div className="flex items-center h-[var(--header-height)] px-4 gap-3">
          {/* Back button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/visits')}
            className="-ml-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {/* Title */}
          <div className="flex-1 min-w-0">
            {detailLoading ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-semibold truncate">
                    {detail?.client.name}
                  </h1>
                  {detail?.currentVisit?.priority === 'URGENT' && (
                    <Badge variant="urgent" className="text-xs shrink-0">
                      Срочно
                    </Badge>
                  )}
                </div>
                {officeName && (
                  <p className="text-xs text-muted-foreground truncate">
                    {officeName}
                  </p>
                )}
                {!officeName &&
                  detail?.client.legalName &&
                  detail.client.legalName !== detail.client.name && (
                    <p className="text-xs text-muted-foreground truncate">
                      {detail.client.legalName}
                    </p>
                  )}
              </>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setVaultSheetOpen(true)}
              title="Пароли клиента"
            >
              <Key className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setAccessSheetOpen(true)}
              title="Кто видит клиента"
            >
              <Users className="h-5 w-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="-mr-2">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem disabled className="text-muted-foreground">
                  ID: {clientId}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Content with Tabs */}
      <ClientProvider
        value={{
          clientId,           // Real client ID for API calls
          visitTargetId,      // Visit target ID (may be virtual office ID)
          officeId,           // Office ID for per-office preferences
          detail,
          detailLoading,
          activeVisit: activeVisit ?? null,
          onCheckIn: handleCheckIn,
          onCheckOut: handleCheckOut,
          onMarkVisited: handleMarkVisited,
          onMarkSkipped: handleMarkSkipped,
          onUndoCheckIn: handleUndoCheckIn,
          onSetUrgent: handleSetUrgent,
          onRemoveUrgent: handleRemoveUrgent,
          onPlanToday: handlePlanToday,
          onCancelPlanToday: handleCancelPlanToday,
          onScheduleVisit: handleScheduleVisit,
          isActionLoading,
        }}
      >
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col">
          <div className="px-4 pt-2">
            <ClientTabNav activeTab={activeTab} onTabChange={handleTabChange} />
          </div>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="main" className="m-0">
              <MainTab />
            </TabsContent>
            <TabsContent value="orders" className="m-0">
              <OrdersTab />
            </TabsContent>
            <TabsContent value="docs" className="m-0">
              <DocsTab />
            </TabsContent>
            <TabsContent value="contacts" className="m-0">
              <ContactsTab />
            </TabsContent>
            <TabsContent value="location" className="m-0">
              <LocationTab />
            </TabsContent>
            <TabsContent value="info" className="m-0">
              <InfoTab />
            </TabsContent>
            <TabsContent value="monitoring" className="m-0">
              <MonitoringTab />
            </TabsContent>
            <TabsContent value="licensing" className="m-0">
              <ClientLicensingTab />
            </TabsContent>
          </div>
        </Tabs>
      </ClientProvider>

      {/* Client Access Sheet */}
      <ClientAccessSheet
        clientId={clientId}
        open={accessSheetOpen}
        onOpenChange={setAccessSheetOpen}
      />

      {/* Vault Sheet */}
      <VaultSheet
        clientId={clientId}
        clientName={detail?.client.name || ''}
        open={vaultSheetOpen}
        onOpenChange={setVaultSheetOpen}
      />
    </div>
  );
}
